import { useListPagination } from "../../hooks/useListState.js";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { edge, isManager, query, requireClient } from "../../lib/supabase.js";
export function StaffAccounts({ context }) {
  const { message, modal } = App.useApp();
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const pagination = useListPagination("list:staff:page", staff.length, null, loading || !!error);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setStaff(
        await query(
          requireClient()
            .from("profiles")
            .select("id,display_name,role,status")
            .eq("academy_id", context.academyId)
            .is("deleted_at", null)
            .order("display_name"),
        ),
      );
    } catch (failure) {
      setError(failure.message);
    } finally {
      setLoading(false);
    }
  }, [context.academyId]);
  useEffect(() => {
    load();
  }, [load]);
  async function create(values) {
    if (!isManager(context.role) || saving) return;
    setSaving(true);
    try {
      await edge("staff-accounts", { action: "create", ...values });
      form.resetFields();
      setOpen(false);
      message.success("직원 계정을 생성했습니다.");
      await load();
    } catch (failure) {
      setError(failure.message);
    } finally {
      setSaving(false);
    }
  }
  function deactivate(row) {
    modal.confirm({
      title: `${row.display_name} 계정을 비활성화할까요?`,
      okText: "비활성화",
      cancelText: "취소",
      onOk: async () => {
        try {
          await edge("staff-accounts", {
            action: "deactivate",
            userId: row.id,
          });
          await load();
        } catch (failure) {
          setError(failure.message);
        }
      },
    });
  }
  return (
    <Card
      className="academy-management-card"
      title="직원 계정"
      extra={
        isManager(context.role) && (
          <Button type="primary" onClick={() => setOpen(true)}>
            직원 계정 추가
          </Button>
        )
      }
    >
      {error && (
        <Alert
          type="error"
          message={error}
          action={<Button onClick={load}>재시도</Button>}
        />
      )}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={staff}
        pagination={{ ...pagination, showSizeChanger: undefined }}
        columns={[
          { title: "이름", dataIndex: "display_name" },
          { title: "역할", dataIndex: "role" },
          {
            title: "상태",
            render: (_, row) => (
              <Tag>{row.status === "active" ? "활성" : "비활성"}</Tag>
            ),
          },
          {
            title: "",
            render: (_, row) =>
              isManager(context.role) &&
              row.id !== context.userId &&
              row.role !== "owner" &&
              row.status === "active" && (
                <Button danger onClick={() => deactivate(row)}>
                  비활성화
                </Button>
              ),
          },
        ]}
      />
      <Modal
        title="직원 계정 추가"
        open={open}
        onCancel={() => !saving && setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="계정 생성"
        cancelText="취소"
      >
        {error && <Alert type="error" message={error} />}
        <Form
          form={form}
          layout="vertical"
          onFinish={create}
          disabled={saving}
          initialValues={{ role: "teacher" }}
        >
          <Form.Item
            name="displayName"
            label="이름"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="이메일"
            rules={[{ required: true, type: "email" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="temporaryPassword"
            label="초기 비밀번호"
            rules={[{ required: true, min: 8 }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="role" label="역할">
            <Select
              options={["admin", "teacher", "kiosk"].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
